import { displayRunName, displayScopeLabel } from "@/utils/payroll-names";
import { useLocale } from "@/hooks/useLocale";
import { ViewAction } from "@/components/table/actions";

import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Search } from "lucide-react";

import { formatDateDmy } from "@/utils/helper";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { payrollRunStatusToVariant } from "@/utils/payrollRunStatusVariant";
import { payrollRunPaymentBadge } from "@/utils/payrollRunPaymentLabel";
import { Pagination } from "@/components/table/Pagination";
import { cn } from "@/utils/cn";
import {
  useListPayrollHistoryQuery,
  type IPayrollHistoryEmployeePreview,
  type IPayrollHistoryMonth,
  type IPayrollHistoryRun,
  type IPayrollHistoryTotalByCurrency,
} from "@/services/(company)/payrollRunsApi";
import { useTranslation } from "react-i18next";

const SCOPE_TONES: Record<string, string> = {
  BRANCH: "bg-warning-muted text-warning",
  DEPARTMENT: "bg-success-muted text-success",
  PAYROLL_GROUP: "bg-primary/10 text-primary",
  EMPLOYEES: "bg-info-muted text-info",
  MIXED: "bg-info-muted text-info",
};

const getInitials = (name: string) =>
  name
    .replace(/[^a-zA-Z\s]/g, "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "?";

const formatAmount = (v: number | string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const safeDate = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : formatDateDmy(d);
};

const Monogram = ({ scopeType, name }: { scopeType: string; name: string }) => (
  <div
    className={cn(
      "size-9 shrink-0 rounded-xs flex items-center justify-center text-xs font-bold",
      SCOPE_TONES[scopeType] ?? "bg-muted text-muted-foreground",
    )}
  >
    {getInitials(name)}
  </div>
);

const EmployeeStack = ({
  previews,
  others,
}: {
  previews: IPayrollHistoryEmployeePreview[];
  others: number;
}) => {
  const { t } = useTranslation(["company"]);
  return (
    <div className="flex items-center gap-2">
      <div className="flex -space-x-2">
        {previews.slice(0, 3).map((e) => (
          <Avatar key={e.id} className="size-7 ring-2 ring-white">
            {e.profileImage && (
              <AvatarImage src={e.profileImage} alt={e.fullName} />
            )}
            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">
              {getInitials(e.fullName)}
            </AvatarFallback>
          </Avatar>
        ))}
      </div>
      {others > 0 && (
        <span className="text-xs font-semibold text-primary">
          {t("company:payrolls.plus_n_others", { count: others })}
        </span>
      )}
    </div>
  );
};

const TotalsCell = ({
  totals,
}: {
  totals: IPayrollHistoryTotalByCurrency[];
}) => {
  if (!totals?.length) return <span className="text-gray-300">—</span>;
  return (
    <div className="flex flex-col">
      {totals.map((t) => (
        <span key={t.currencyCode} className="text-xs text-gray-700 truncate">
          {t.currencyCode} {formatAmount(t.net)}
        </span>
      ))}
    </div>
  );
};

const StatusBadge = ({ row }: { row: IPayrollHistoryRun }) => {
  const { t } = useTranslation(["company", "common"]);
  // "Paid" only once every payable employee is (B34); translated, where the
  // API's `statusLabel` is English.
  const badge = payrollRunPaymentBadge(row, t);
  return (
    <Badge variant={badge?.variant ?? payrollRunStatusToVariant(row.status)}>
      {badge?.label ?? row.statusLabel ?? row.status}
    </Badge>
  );
};

const Row = ({
  row,
  onView,
}: {
  row: IPayrollHistoryRun;
  onView: () => void;
}) => {
  const { t } = useTranslation(["company"]);
  const { formatDate } = useLocale();
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onView();
        }
      }}
      className="grid grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr_1fr_0.9fr_0.5fr] items-center gap-3 px-4 py-3 border-t border-border cursor-pointer hover:bg-primary/5 transition-colors duration-150 outline-hidden"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Monogram scopeType={row.scopeType} name={row.name} />
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {displayRunName(t, row.name, formatDate)}
          </span>
          <span className="text-[11px] text-gray-400 truncate">
            {displayScopeLabel(t, row.scopeLabel)}
          </span>
        </div>
      </div>
      <span className="text-sm text-gray-700 truncate">{row.branchName}</span>
      <span className="text-sm text-gray-700 truncate">
        {row.currencies?.join(", ") || "—"}
      </span>
      <EmployeeStack
        previews={row.employeePreviews ?? []}
        others={row.additionalEmployeesCount}
      />
      <TotalsCell totals={row.totalsByCurrency} />
      <span className="text-sm text-gray-700">{safeDate(row.payDate)}</span>
      <StatusBadge row={row} />
      <ViewAction
        onClick={(e) => {
          e.stopPropagation();
          onView();
        }}
        className="text-primary justify-self-end"
        iconSize={16}
      />
    </div>
  );
};

const MonthAccordion = ({
  group,
  expanded,
  onToggle,
  onRowView,
}: {
  group: IPayrollHistoryMonth;
  expanded: boolean;
  onToggle: () => void;
  onRowView: (id: number) => void;
}) => {
  const { t } = useTranslation(["company", "common"]);
  return (
    <div className="rounded-xs border border-border bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="grid grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr_1fr_0.9fr_0.5fr] items-center gap-3 w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 text-start"
      >
        <div className="flex items-center gap-3 min-w-0">
          {expanded ? (
            <ChevronDown size={16} className="text-primary shrink-0" />
          ) : (
            <ChevronRight size={16} className="text-primary shrink-0" />
          )}
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-semibold text-gray-900">
              {group.month}
            </span>
            <span className="text-[11px] text-gray-400">
              {t("company:payrolls.run_count", { count: group.runCount })} ·{" "}
              {t("company:payrolls.employee_count", {
                count: group.totalEmployees,
              })}
            </span>
          </div>
        </div>
        <span />
        <span />
        <span />
        <div className="flex flex-col gap-0.5 min-w-0">
          {(group.summary?.totalsByCurrency ?? []).map((t) => (
            <span
              key={t.currencyCode}
              className="text-xs font-semibold text-gray-900 truncate"
            >
              {t.currencyCode} {formatAmount(t.net)}
            </span>
          ))}
        </div>
        <span />
        <span />
        <span />
      </button>
      {expanded && group.runs.length > 0 && (
        <div className="flex flex-col">
          {group.runs.map((r) => (
            <Row key={r.id} row={r} onView={() => onRowView(r.id)} />
          ))}
        </div>
      )}
    </div>
  );
};

interface IProps {
  onRowView: (id: number) => void;
  branchId?: number;
  status?: string;
  onCountChange?: (count: number) => void;
}

export const PayrollHistoryTable = ({
  onRowView,
  branchId,
  status,
  onCountChange,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const autoExpandedRef = useRef(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, branchId, status]);

  const { currentData, isFetching } = useListPayrollHistoryQuery({
    page,
    limit: 10,
    ...(search ? { search } : {}),
    ...(branchId ? { branchId } : {}),
    ...(status ? { status } : {}),
  });

  const months = useMemo(() => currentData?.data ?? [], [currentData]);
  const totalPages = currentData?.meta?.totalPages ?? 1;
  const historyCount = currentData?.meta?.tabCounts?.history;

  useEffect(() => {
    if (typeof historyCount === "number") onCountChange?.(historyCount);
  }, [historyCount, onCountChange]);

  useEffect(() => {
    if (autoExpandedRef.current) return;
    if (months.length > 0) {
      autoExpandedRef.current = true;
      setExpandedKeys(new Set([months[0].monthKey]));
    }
  }, [months]);

  const toggle = (key: string) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
      <div className="flex items-center gap-2 border border-input rounded-xs h-9 px-3 w-80 bg-white">
        <Search size={14} className="text-gray-400" />
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t("company:payrolls.search_branch_name_status")}
          className="flex-1 bg-transparent text-xs placeholder:text-gray-400 focus:outline-hidden"
        />
      </div>

      {/* Header row */}
      <div className="grid grid-cols-[2fr_1fr_1fr_1.2fr_1.2fr_1fr_0.9fr_0.5fr] gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
        <span>{t("company:payrolls.run")}</span>
        <span>{t("common:ui_kit.branch")}</span>
        <span>{t("company:payrolls.currencies")}</span>
        <span>{t("company:payrolls.employees")}</span>
        <span>{t("company:payrolls.net_pay")}</span>
        <span>{t("company:payrolls.pay_date")}</span>
        <span>{t("company:payrolls.status")}</span>
        <span className="justify-self-end">{t("company:payrolls.action")}</span>
      </div>

      <div className="flex flex-col gap-3">
        {isFetching && months.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {t("company:payrolls.loading_history")}
          </div>
        ) : months.length === 0 ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {t("company:payrolls.no_payroll_history")}
          </div>
        ) : (
          months.map((m) => (
            <MonthAccordion
              key={m.monthKey}
              group={m}
              expanded={expandedKeys.has(m.monthKey)}
              onToggle={() => toggle(m.monthKey)}
              onRowView={onRowView}
            />
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-2">
          <Pagination
            totalPages={totalPages}
            currentPage={page}
            onPageChange={setPage}
          />
        </div>
      )}
    </div>
  );
};
